import React from "react";
import {
    ApartmentOutlined,
    BankOutlined,
    CalendarOutlined,
    EnvironmentOutlined,
    FileTextOutlined,
    IdcardOutlined,
    MailOutlined,
    PhoneOutlined,
    UserOutlined
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {TICKET_STATUS_COLORS} from "constants/ticket";
import {TicketStatusTag} from "../common/TicketStatusTag";
import {formatDateTime, getManagerCompanyLabel} from "../common/ticketUtils";
import {InfoBand, InfoColumn, InfoLabel, InfoValue} from "./TicketDetail.styles";

export const InfoItem = ({title, icon, children}) => (
    <div>
        <InfoLabel>
            {icon}
            {title}
        </InfoLabel>
        <InfoValue>{children || "-"}</InfoValue>
    </div>
);

export const TicketInfoBand = ({ticket, customerView}) => {
    const {t} = useTranslation(["tickets"]);
    const category = ticket.ticketType?.category;
    const label = key => t(`detail.${key}`, {ns: "tickets"});

    const statusItem = (
        <InfoItem key="status" title={label("status")} icon={<FileTextOutlined />}>
            <TicketStatusTag status={ticket.status} darkText />
        </InfoItem>
    );
    const zonesItem = (
        <InfoItem key="zones" title={label("zones")} icon={<EnvironmentOutlined />}>
            {ticket.zones?.map(zone => zone.name).join(", ")}
        </InfoItem>
    );
    const ownerItem = (key, icon, value) => (
        <InfoItem key={key} title={label(key)} icon={icon}>
            {value}
        </InfoItem>
    );
    const managerCompanyItem = ownerItem(
        "managerCompany",
        <ApartmentOutlined />,
        getManagerCompanyLabel(ticket.ownerDetail?.managerCompany)
    );
    const createdItem = (
        <InfoItem key="created" title={label("created")} icon={<CalendarOutlined />}>
            {formatDateTime(ticket.createdAt)}
        </InfoItem>
    );

    const columns = [];
    if (customerView) {
        columns.push([ownerItem("branch", <BankOutlined />, ticket.owner?.name)], [statusItem]);
    } else {
        if (category !== "other") {
            columns.push([statusItem, zonesItem]);
        }
        if (category === "courier") {
            columns.push(
                [ownerItem("courier", <UserOutlined />, ticket.owner?.name), managerCompanyItem],
                [ownerItem("courierTckn", <IdcardOutlined />, ticket.owner?.tckn)]
            );
        } else if (category === "company") {
            columns.push(
                [
                    ownerItem("company", <UserOutlined />, ticket.ownerDetail?.companyName),
                    managerCompanyItem
                ],
                [ownerItem("branch", <BankOutlined />, ticket.owner?.name)]
            );
        } else if (["onboarding_company", "onboarding_agency"].includes(category)) {
            columns.push([ownerItem("application", <FileTextOutlined />, ticket.owner?.name)]);
        } else if (category === "other") {
            const {phoneNumber, city, district} = ticket.ownerDetail || {};
            columns.push(
                [statusItem, ownerItem("phone", <PhoneOutlined />, phoneNumber)],
                [
                    ownerItem("fullName", <UserOutlined />, ticket.owner?.name),
                    ownerItem(
                        "location",
                        <EnvironmentOutlined />,
                        [city, district].filter(Boolean).join(" / ")
                    )
                ],
                [ownerItem("email", <MailOutlined />, ticket.owner?.email)]
            );
        }
    }
    columns.push([createdItem]);

    return (
        <InfoBand $background={TICKET_STATUS_COLORS[ticket.status]?.background}>
            {columns.map((items, index) => (
                <InfoColumn key={index}>{items}</InfoColumn>
            ))}
        </InfoBand>
    );
};
